import type { Car } from '@taler/api-client'
import { carKeys } from '@taler/api-client'
import { useQueries } from '@tanstack/vue-query'
import { computed, type ComputedRef, onScopeDispose, shallowRef, watch } from 'vue'
import { useApi } from '@/app/api'

export interface UseCarPhotosOptions {
  readonly ledgerId: () => string | undefined
  readonly cars: () => readonly Car[]
}

/** An object URL per car that has a photo, keyed by car id; revoked when replaced or unmounted. */
export function useCarPhotos(options: UseCarPhotosOptions): ComputedRef<ReadonlyMap<string, string>> {
  const api = useApi()
  const photoCars = computed(() => options.cars().filter((car) => car.hasPhoto))

  const photos = useQueries({
    queries: computed(() =>
      photoCars.value.map((car) => ({
        queryKey: carKeys.photo(options.ledgerId() ?? '', car.id),
        queryFn: ({ signal }: { signal: AbortSignal }) => api.cars.photo(options.ledgerId()!, car.id, signal),
        staleTime: Infinity,
        retry: false,
      })),
    ),
  })

  const created = new Map<string, { blob: Blob; url: string }>()
  const urls = shallowRef<ReadonlyMap<string, string>>(new Map())
  watch(
    () => photoCars.value.map((car, index) => [car.id, photos.value[index]?.data] as const),
    (entries) => {
      const next = new Map<string, { blob: Blob; url: string }>()
      for (const [carId, download] of entries) {
        if (download === undefined) continue
        const previous = created.get(carId)
        next.set(
          carId,
          previous?.blob === download.blob
            ? previous
            : { blob: download.blob, url: URL.createObjectURL(download.blob) },
        )
      }
      for (const [carId, { url }] of created) {
        if (next.get(carId)?.url !== url) URL.revokeObjectURL(url)
      }
      created.clear()
      for (const [carId, entry] of next) created.set(carId, entry)
      urls.value = new Map([...next].map(([carId, { url }]) => [carId, url]))
    },
    { immediate: true },
  )
  onScopeDispose(() => {
    for (const { url } of created.values()) URL.revokeObjectURL(url)
  })

  return computed(() => urls.value)
}
